import '@skyscanner-internal/backpack-web/bpk-stylesheets/base';
import '@skyscanner-internal/backpack-web/bpk-stylesheets/base.css';
import '@skyscanner-internal/backpack-web/bpk-stylesheets/font';
import { afterDomContentLoaded } from 'saddlebag-browser';

import './index.scss';
import './public-path';
import { initGtmClient } from './gtmClient';
import { loadExternalScripts } from './scriptLoader';
import { logErrorWrapper, logWarnWrapper } from './services/logWrapper';

import type { InternalProps } from './types/globals';

const RouteName = Object.freeze({
  MONTH: 'month',
  NOT_FOUND: 'notFound',
});

const onPageLoaded = async () => {
  const { initStrevda } = await import(
    /* webpackMode: "eager" */ './services/strevdaRuntime/init'
  );
  initGtmClient();
  initStrevda();
};

const loadAppRoot = async (props: InternalProps) => {
  const React = await import(/* webpackMode: "eager" */ 'react');
  const { createRoot } = await import(
    /* webpackMode: "eager" */ 'react-dom/client'
  );
  const { default: App } = await import(
    /* webpackMode: "eager", webpackChunkName: "MonthView" */ './components/App'
  );
  const { RouterProvider, createBrowserRouter } = await import(
    /* webpackMode: "eager" */ 'react-router'
  );
  const container = document.getElementById('app-root');
  if (!container) {
    throw new Error('App root container not found');
  }
  const router = createBrowserRouter([
    { path: '*', element: React.createElement(App, props) },
  ]);
  const root = createRoot(container);
  root.render(React.createElement(RouterProvider, { router }));
};

(async () => {
  try {
    const { setupErrorHandling } = await import(
      /* webpackMode: "eager" */ './utils/errorHandling/index'
    );

    setupErrorHandling();
  } catch (error) {
    logErrorWrapper(error as Error, {
      message: 'Failed to setup errorHandling',
      component: 'index',
    });
  }
  try {
    await loadExternalScripts();
  } catch (error) {
    logErrorWrapper(error as Error, {
      message: 'Failed to load dependencies',
      component: 'index',
    });
  }
  try {
    const { debugControlsInit, localisationInit } = await import(
      /* webpackMode: "eager" */ './bootstrap'
    );
    const { default: hydrate } = await import('./hydrate');

    debugControlsInit();

    const onDomContentLoaded = async () => {
      const { routeName } = window.__internal;

      // Only month view route requires modification of props and initialisation of the client React app.
      if (routeName === RouteName.MONTH) {
        const data = window.__internal;
        const { props } = window.__internal;

        // There is a known case of undefined props which we don't know the cause of yet.
        if (!props) return;

        // initTrackingPreferences must run before loadAppRoot so that isKetchMode() is
        // set before strevda-runtime calls requestAdsFromGAM. All config is already
        // available on cookieBannerProps at DOMContentLoaded time.
        const { cookieBannerProps } = window.__internal;
        if (cookieBannerProps) {
          const { initTrackingPreferences } =
            await import('saddlebag-tracking-preferences');
          const {
            consentIdServiceUrl = '/g/purpose-scoped-id-service/identifiers/consent-id',
            enableConsentIdService = false,
            enableCookieVersionRollback = false,
            enableKetchCMP = false,
            enableKetchPsidSync = false,
            enableLegacyConsentSync = false,
            enablePurposeScopedIdExistingGDPRUsers = false,
            enablePurposeScopedIdNonRegulatedMarkets = false,
            enablePurposeScopedIdService,
            ketchConsentCookieName = '',
            ketchScriptUrl = '',
            ketchTCFStorageKey = '',
            market,
            purposeScopedIdServiceKetchCmpUrl = '/g/purpose-scoped-id-service/identifiers/refresh',
            purposeScopedIdServiceLegacyCmpUrl,
          } = cookieBannerProps;
          initTrackingPreferences({
            consentIdServiceUrl,
            enableConsentIdService,
            enableCookieVersionRollback,
            enableKetchCMP,
            enableKetchPsidSync,
            enableLegacyConsentSync,
            enablePurposeScopedIdExistingGDPRUsers,
            enablePurposeScopedIdNonRegulatedMarkets,
            enablePurposeScopedIdService,
            consumer: 'month-view-page',
            market,
            ketchConsentCookieName,
            ketchScriptUrl,
            ketchTCFStorageKey,
            purposeScopedIdServiceKetchCmpUrl,
            purposeScopedIdServiceLegacyCmpUrl,
          });
        }

        props.localisation = localisationInit(
          props.cldr,
          props.culture,
          props.translations,
        );
        props.adverts = data.adverts;
        props.device = data.device;
        // assumption: this deletion was to prevent the init being rerun
        delete window.__internal.props;

        try {
          await loadAppRoot(props);
        } catch (err) {
          logErrorWrapper(err as Error, {
            message: 'Failed to load app root.',
            component: 'index',
          });
        }

        // Hydrating local components from the server.
        try {
          await hydrate();
        } catch (err) {
          logErrorWrapper(err as Error, {
            message: 'Failed to hydrate local-components.',
            component: 'index',
          });
        }

        onPageLoaded().catch((err) => {
          logErrorWrapper(err as Error, {
            message: 'Failed to initialise client side after page loaded',
            component: 'index',
          });
        });
        return;
      }

      if (routeName === RouteName.NOT_FOUND) {
        try {
          await hydrate();
        } catch (err) {
          logErrorWrapper(err as Error, {
            message: 'Failed to hydrate local-components.',
            component: 'index',
          });
        }
        return;
      }

      logWarnWrapper({
        message: `Route name "${routeName}" is not valid.`,
        component: 'index',
      });
    };

    afterDomContentLoaded(onDomContentLoaded);
  } catch (err) {
    logErrorWrapper(err as Error, {
      message: 'Failed to initialise client side',
      component: 'index',
    });
  }
})();
